import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Domain {
  id: number;
  domain_name: string;
}

interface NameTldData {
  name: string;
  tld: string;
  status: 'available' | 'taken';
  exp_date?: string | null;
}

interface NamesTldProps {
  selectedDomain: string | null;
  domains: Domain[];
  onDomainChange: (domain: string) => void;
  namesTldData: NameTldData[];
}

const NamesTld: React.FC<NamesTldProps> = ({
  selectedDomain,
  domains,
  onDomainChange,
  namesTldData,
}) => {
  const { t } = useTranslation();

  const availableData = useMemo(
    () => namesTldData.filter((item) => item.status === 'available'),
    [namesTldData]
  );

  const takenData = useMemo(
    () => namesTldData.filter((item) => item.status === 'taken'),
    [namesTldData]
  );

  const dataPerPage = 6;
  const [currentPageAvail, setCurrentPageAvail] = useState(1);
  const [currentPageTaken, setCurrentPageTaken] = useState(1);

  const totalPagesAvail = Math.ceil(availableData.length / dataPerPage);
  const totalPagesTaken = Math.ceil(takenData.length / dataPerPage);

  const currentAvail = availableData.slice((currentPageAvail - 1) * dataPerPage, currentPageAvail * dataPerPage);
  const currentTaken = takenData.slice((currentPageTaken - 1) * dataPerPage, currentPageTaken * dataPerPage);

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
    setCurrentPageAvail(1);
    setCurrentPageTaken(1);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4"></div>
        <h3 className="text-3xl font-bold">{t('namesTld.title')}</h3>
      </div>

      <p className="text-gray-700 text-xl mb-4">
        {t('namesTld.description')}
      </p>

      {selectedDomain && (
        <div className="mb-4">
          <label htmlFor="domain-select" className="block mb-2 text-lg font-medium">
            <strong>{t('namesTld.selectDomain')}</strong>
          </label>
          <select
            id="domain-select"
            value={selectedDomain}
            onChange={handleDomainChange}
            className="w-full max-w-md p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md"
          >
            {domains.map((d) => (
              <option key={d.id} value={d.domain_name}>
                {d.domain_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
        {/* Columna A: DISPONIBLES */}
        <div className="w-full md:w-1/2">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col justify-between" style={{ minHeight: '600px' }}>
            <div>
              <div className="flex items-center mb-6">
                <div className="w-2 h-6 bg-gray-800 mr-4"></div>
                <h3 className="text-2xl font-bold">{t('namesTld.availableTitle')}</h3>
              </div>

              {currentAvail.length === 0 ? (
                <p className="text-center">{t('namesTld.noAvailable')}</p>
              ) : (
                <table className="min-w-full bg-white table-auto">
                  <thead>
                    <tr className="bg-green-200 text-gray-800">
                      <th className="py-3 px-6 text-left text-sm font-semibold">{t('namesTld.columns.domain')}</th>
                      <th className="py-3 px-6 text-left text-sm font-semibold">{t('namesTld.columns.tld')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAvail.map((item, idx) => (
                      <tr key={idx} className="text-gray-700 bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
                        <td className="py-3 px-6 border">{item.name}</td>
                        <td className="py-3 px-6 border">.{item.tld}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {availableData.length > 0 && (
              <div className="mt-6 flex justify-center items-center space-x-4">
                <button onClick={() => setCurrentPageAvail((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPageAvail === 1}
                  className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600">
                  {t('pagination.previous')}
                </button>
                <span>{t('pagination.pageInfo', { current: currentPageAvail, total: totalPagesAvail })}</span>
                <button onClick={() => setCurrentPageAvail((prev) => Math.min(prev + 1, totalPagesAvail))}
                  disabled={currentPageAvail === totalPagesAvail}
                  className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600">
                  {t('pagination.next')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Columna B: TOMADOS */}
        <div className="w-full md:w-1/2">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col justify-between" style={{ minHeight: '600px' }}>
            <div>
              <div className="flex items-center mb-6">
                <div className="w-2 h-6 bg-gray-800 mr-4"></div>
                <h3 className="text-2xl font-bold">{t('namesTld.takenTitle')}</h3>
              </div>

              {currentTaken.length === 0 ? (
                <p className="text-center">{t('namesTld.noTaken')}</p>
              ) : (
                <table className="min-w-full bg-white table-auto">
                  <thead>
                    <tr className="bg-red-200 text-gray-800">
                      <th className="py-3 px-6 text-left text-sm font-semibold">{t('namesTld.columns.domain')}</th>
                      <th className="py-3 px-6 text-left text-sm font-semibold">{t('namesTld.columns.tld')}</th>
                      <th className="py-3 px-6 text-left text-sm font-semibold">{t('namesTld.columns.expiration')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTaken.map((item, idx) => (
                      <tr key={idx} className="text-gray-700 bg-gray-50 even:bg-gray-100 hover:bg-gray-200">
                        <td className="py-3 px-6 border">{item.name}</td>
                        <td className="py-3 px-6 border">.{item.tld}</td>
                        <td className="py-3 px-6 border">{item.exp_date || t('namesTld.unknown')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {takenData.length > 0 && (
              <div className="mt-6 flex justify-center items-center space-x-4">
                <button onClick={() => setCurrentPageTaken((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPageTaken === 1}
                  className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600">
                  {t('pagination.previous')}
                </button>
                <span>{t('pagination.pageInfo', { current: currentPageTaken, total: totalPagesTaken })}</span>
                <button onClick={() => setCurrentPageTaken((prev) => Math.min(prev + 1, totalPagesTaken))}
                  disabled={currentPageTaken === totalPagesTaken}
                  className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600">
                  {t('pagination.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NamesTld;
